/**
 * F3 atajo factura+remito: asignación agrupada por depósito (no una fila por línea).
 * Run: npx tsx --test src/modules/Ventas/remitoAsignacionAgrupada.test.ts
 */
import type {
  FacturaItem,
  VentaRemito,
  VentaRemitoDetalle,
  VentaRemitoEntregaItem,
  VentaRemitoItemOpcionesDeposito,
  VentaRemitoOpcionesDeposito,
} from "../../lib/api";
import { toNum } from "../../lib/utils";
import {
  coveringDepositos,
  depStock,
  depositoRetiroModo,
  itemQtyNeeded,
  splitQtyAcrossDepositos,
} from "../RemitosVtas/remitoDepositoRetiro";

export function clienteEsDeudor(saldo: number | null | undefined): boolean {
  return toNum(saldo) < 0;
}

export type AsignacionLinea = {
  facturaItemId: number;
  nombre: string;
  cantidad: number;
};

export type AsignacionGrupo = {
  depositoId: number;
  depositoNombre: string;
  sucursalNombre?: string;
  items: AsignacionLinea[];
};

export type SharedCoveringDep = {
  id: number;
  nombre: string;
};

export type AsignacionPlan = {
  modo: "single_depot" | "grouped" | "blocked";
  sucursalId?: number;
  sucursalNombre?: string;
  sharedCovering: SharedCoveringDep[];
  selectedDepositoId: number | null;
  groups: AsignacionGrupo[];
  insufficient: AsignacionLinea[];
  /** facturaItemId → allocations [{id, cantidad}] */
  allocationsByFacturaItemId: Record<number, { id: number; cantidad: number }[]>;
};

function itemLabel(row: VentaRemitoItemOpcionesDeposito): string {
  return row.nombreProducto || row.codigoProducto || `Ítem ${row.id}`;
}

function preferredCoveringId(row: VentaRemitoItemOpcionesDeposito): number | undefined {
  const covering = coveringDepositos(row);
  if (covering.length === 0) return undefined;
  return covering[0]?.id;
}

function autoAllocationsForRow(
  row: VentaRemitoItemOpcionesDeposito,
): { id: number; cantidad: number }[] | null {
  const modo = depositoRetiroModo(row);
  const q = itemQtyNeeded(row);
  if (modo === "insuficiente") return null;
  if (modo === "auto" || modo === "radio") {
    const id = preferredCoveringId(row);
    if (id == null) return null;
    return [{ id, cantidad: q }];
  }
  // combinar: partir Q entre depósitos con stock
  const parts = splitQtyAcrossDepositos(row.depositos || [], q);
  if (parts.length === 0) return null;
  const sum = parts.reduce((a, p) => a + p.cantidad, 0);
  if (sum + 1e-9 < q) return null;
  return parts;
}

function sharedCoveringAcrossAll(
  items: VentaRemitoItemOpcionesDeposito[],
): SharedCoveringDep[] {
  if (items.length === 0) return [];
  let shared: Set<number> | null = null;
  const names = new Map<number, string>();
  for (const row of items) {
    const covering = coveringDepositos(row);
    for (const d of covering) names.set(d.id, d.nombre || `Depósito ${d.id}`);
    const ids = new Set(covering.map((d) => d.id));
    shared = shared == null ? ids : new Set([...shared].filter((id) => ids.has(id)));
  }
  return [...(shared || [])]
    .sort((a, b) => a - b)
    .map((id) => ({ id, nombre: names.get(id) || `Depósito ${id}` }));
}

function groupsFromAllocations(
  items: VentaRemitoItemOpcionesDeposito[],
  allocationsByFacturaItemId: Record<number, { id: number; cantidad: number }[]>,
  sucursalNombre?: string,
): AsignacionGrupo[] {
  const byDep = new Map<number, AsignacionGrupo>();
  const depName = new Map<number, string>();
  for (const row of items) {
    for (const d of row.depositos || []) {
      depName.set(d.id, d.nombre || `Depósito ${d.id}`);
    }
  }
  for (const row of items) {
    const allocs = allocationsByFacturaItemId[row.id] || [];
    for (const a of allocs) {
      let group = byDep.get(a.id);
      if (!group) {
        group = {
          depositoId: a.id,
          depositoNombre: depName.get(a.id) || `Depósito ${a.id}`,
          sucursalNombre,
          items: [],
        };
        byDep.set(a.id, group);
      }
      group.items.push({
        facturaItemId: row.id,
        nombre: itemLabel(row),
        cantidad: a.cantidad,
      });
    }
  }
  return [...byDep.values()].sort((a, b) =>
    a.depositoNombre.localeCompare(b.depositoNombre, "es", { sensitivity: "base" }),
  );
}

/**
 * Plan de asignación agrupada.
 * - Un depósito cubre todas las líneas → single_depot (elige uno).
 * - Stock partido → grouped (propuesta auto, confirmar).
 * - Algún producto sin stock → blocked (Confirmar off).
 */
export function buildAsignacionPlan(
  opciones: VentaRemitoOpcionesDeposito | null | undefined,
  selectedDepositoId: number | null = null,
): AsignacionPlan {
  const items = opciones?.items || [];
  const sucursalNombre = opciones?.sucursalNombre;
  const insufficient: AsignacionLinea[] = [];
  for (const row of items) {
    if (depositoRetiroModo(row) === "insuficiente") {
      insufficient.push({
        facturaItemId: row.id,
        nombre: itemLabel(row),
        cantidad: itemQtyNeeded(row),
      });
    }
  }

  if (insufficient.length > 0) {
    const allocationsByFacturaItemId: Record<number, { id: number; cantidad: number }[]> = {};
    for (const row of items) {
      if (depositoRetiroModo(row) === "insuficiente") continue;
      const alloc = autoAllocationsForRow(row);
      if (alloc) allocationsByFacturaItemId[row.id] = alloc;
    }
    return {
      modo: "blocked",
      sucursalId: opciones?.sucursalId,
      sucursalNombre,
      sharedCovering: [],
      selectedDepositoId: null,
      groups: groupsFromAllocations(items, allocationsByFacturaItemId, sucursalNombre),
      insufficient,
      allocationsByFacturaItemId,
    };
  }

  const shared = sharedCoveringAcrossAll(items);
  if (shared.length > 0) {
    const chosen =
      selectedDepositoId != null && shared.some((d) => d.id === selectedDepositoId)
        ? selectedDepositoId
        : shared.length === 1
          ? shared[0].id
          : selectedDepositoId;
    const allocationsByFacturaItemId: Record<number, { id: number; cantidad: number }[]> = {};
    if (chosen != null) {
      for (const row of items) {
        allocationsByFacturaItemId[row.id] = [{ id: chosen, cantidad: itemQtyNeeded(row) }];
      }
    }
    return {
      modo: "single_depot",
      sucursalId: opciones?.sucursalId,
      sucursalNombre,
      sharedCovering: shared,
      selectedDepositoId: chosen ?? null,
      groups:
        chosen != null
          ? groupsFromAllocations(items, allocationsByFacturaItemId, sucursalNombre)
          : [],
      insufficient: [],
      allocationsByFacturaItemId,
    };
  }

  const allocationsByFacturaItemId: Record<number, { id: number; cantidad: number }[]> = {};
  for (const row of items) {
    const alloc = autoAllocationsForRow(row);
    if (!alloc) {
      insufficient.push({
        facturaItemId: row.id,
        nombre: itemLabel(row),
        cantidad: itemQtyNeeded(row),
      });
      continue;
    }
    allocationsByFacturaItemId[row.id] = alloc;
  }
  if (insufficient.length > 0) {
    return {
      modo: "blocked",
      sucursalId: opciones?.sucursalId,
      sucursalNombre,
      sharedCovering: [],
      selectedDepositoId: null,
      groups: groupsFromAllocations(items, allocationsByFacturaItemId, sucursalNombre),
      insufficient,
      allocationsByFacturaItemId,
    };
  }
  return {
    modo: "grouped",
    sucursalId: opciones?.sucursalId,
    sucursalNombre,
    sharedCovering: [],
    selectedDepositoId: null,
    groups: groupsFromAllocations(items, allocationsByFacturaItemId, sucursalNombre),
    insufficient: [],
    allocationsByFacturaItemId,
  };
}

export function canConfirmAsignacion(plan: AsignacionPlan): boolean {
  if (plan.modo === "blocked" || plan.insufficient.length > 0) return false;
  if (plan.modo === "single_depot" && plan.selectedDepositoId == null) return false;
  const ids = Object.keys(plan.allocationsByFacturaItemId);
  return ids.length > 0;
}

export function productsLabel(items: AsignacionLinea[], maxNames = 3): string {
  if (items.length === 0) return "";
  const names = items.map((i) => i.nombre);
  if (names.length <= maxNames) return names.join(", ");
  const head = names.slice(0, maxNames).join(", ");
  return `${head} y ${names.length - maxNames} más`;
}

export function countLabel(items: AsignacionLinea[]): string {
  const n = items.length;
  return n === 1 ? "1 ítem" : `${n} ítems`;
}

function comboOf(
  row: Pick<FacturaItem | VentaRemitoDetalle, "comboGroup" | "combo_group">,
): string | null {
  const g = row.comboGroup ?? row.combo_group;
  if (g == null || g === "") return null;
  return String(g);
}

function productoIdOf(row: {
  productoId?: number | null;
  producto_id?: number | null;
}): number | null {
  const raw = row.productoId ?? row.producto_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Empareja líneas de factura (preview) con detalles del remito recién creado.
 */
export function matchFacturaItemToRemitoDetalle(
  facturaItemId: number,
  facturaItems: FacturaItem[],
  remitoItems: VentaRemitoDetalle[],
): VentaRemitoDetalle | null {
  const fd = facturaItems.find((i) => Number(i.id) === Number(facturaItemId));
  if (!fd) return null;
  const gid = comboOf(fd);
  const pid = productoIdOf(fd);
  if (gid) {
    const comboMatches = remitoItems.filter((rd) => comboOf(rd) === gid);
    if (pid != null) {
      const byProd = comboMatches.find((rd) => productoIdOf(rd) === pid);
      if (byProd) return byProd;
    }
    const fdLegs = facturaItems
      .filter((i) => comboOf(i) === gid)
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || Number(a.id) - Number(b.id));
    const rdLegs = [...comboMatches].sort((a, b) => Number(a.id) - Number(b.id));
    const idx = fdLegs.findIndex((i) => Number(i.id) === Number(facturaItemId));
    if (idx >= 0 && idx < rdLegs.length) return rdLegs[idx];
  }
  if (pid != null) {
    const byProd = remitoItems.find((rd) => productoIdOf(rd) === pid);
    if (byProd) return byProd;
  }
  const orderedFd = [...facturaItems].sort(
    (a, b) => (a.orden ?? 0) - (b.orden ?? 0) || Number(a.id) - Number(b.id),
  );
  const orderedRd = [...remitoItems].sort((a, b) => Number(a.id) - Number(b.id));
  const idx = orderedFd.findIndex((i) => Number(i.id) === Number(facturaItemId));
  if (idx >= 0 && idx < orderedRd.length) return orderedRd[idx];
  return null;
}

export function mapAsignacionToRemitoEntrega(
  remito: VentaRemito,
  plan: AsignacionPlan,
  facturaItems: FacturaItem[],
): VentaRemitoEntregaItem[] {
  const remitoItems = remito.items || [];
  const used = new Set<number>();
  const out: VentaRemitoEntregaItem[] = [];
  for (const [facturaItemIdStr, allocs] of Object.entries(plan.allocationsByFacturaItemId)) {
    const facturaItemId = Number(facturaItemIdStr);
    const rd = matchFacturaItemToRemitoDetalle(facturaItemId, facturaItems, remitoItems);
    if (!rd?.id || used.has(rd.id)) continue;
    used.add(rd.id);
    const qty = allocs.reduce((a, p) => a + p.cantidad, 0);
    const payload: VentaRemitoEntregaItem = {
      id: rd.id,
      cantidad_entregada: qty,
      entregado: true,
    };
    if (allocs.length === 1) {
      payload.depositoId = allocs[0].id;
      payload.depositos = [{ id: allocs[0].id, cantidad: allocs[0].cantidad }];
    } else {
      payload.depositos = allocs.map((a) => ({ id: a.id, cantidad: a.cantidad }));
      payload.depositoIds = allocs.map((a) => a.id);
    }
    out.push(payload);
  }
  return out;
}

/** Stock disponible mostrado junto al depósito (mínimo entre líneas que lo eligen). */
export function sharedDepDetail(
  dep: SharedCoveringDep,
  opciones: VentaRemitoOpcionesDeposito | null | undefined,
): string {
  const items = opciones?.items || [];
  if (items.length === 0) return "stock alcanza";
  let minStock = Infinity;
  for (const row of items) {
    const d = (row.depositos || []).find((x) => x.id === dep.id);
    if (!d) continue;
    minStock = Math.min(minStock, depStock(d));
  }
  const n = items.length;
  const count = n === 1 ? "1 ítem" : `${n} ítems`;
  if (!Number.isFinite(minStock)) return `${count} · stock alcanza`;
  return `${count} · stock alcanza (disp. ≥ ${Math.floor(minStock)})`;
}
